import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Check,
  CheckCheck,
  FileText,
  FolderInput,
  Loader2,
  Paperclip,
  Sparkles,
  Undo2,
  UserCheck
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { MattersAPI } from "@/api/matters";
import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { MatterDTO } from "@/api/types/matter";
import type {
  OutgoingIntakeSubmissionView,
  OutgoingIntakeTargetSuggestion
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  CASE_HOME_SEGMENT,
  casePath,
  matterCaseRef
} from "@/features/caseIde/routes";
import { formatGroupAnswerValue } from "@/features/foreignNationalOutgoingIntakes/utils/intakeReviewPhrasing";
import { getPetitionerName } from "@/utils/matterDisplay";

import { SaveToPicker } from "../../components/SaveToPicker";
import { useRequestsIde } from "../../context/RequestsIdeContext";
import { toastEmailFailed } from "../../linkToasts";
import { ASSISTANT_NAME } from "../../model";
import type { ReviewRow, SubmissionReview } from "../useSubmissionReview";

const Section: FC<{
  icon: ReactNode;
  title: string;
  trailing?: string;
  children: ReactNode;
}> = ({ icon, title, trailing, children }) => (
  <section className="flex flex-col gap-2">
    <div className="flex items-center gap-1.5">
      <span className="text-fg-3 [&_svg]:size-3.5">{icon}</span>
      <h3 className="text-base font-medium text-fg">{title}</h3>
      <span className="flex-1" />
      {trailing ? <span className="text-sm text-fg-3">{trailing}</span> : null}
    </div>
    {children}
  </section>
);

const NO_CASE = "__none__";

const matterLabel = (m: MatterDTO): string => {
  const visa = String(m.case?.visa ?? "").trim() || "Case";
  const who = getPetitionerName(m);
  const status = String(m.case?.status ?? "").trim();
  return (
    [visa, who].filter(Boolean).join(" · ") + (status ? ` (${status})` : "")
  );
};

type DocItem = { id: string; row: ReviewRow; fileId: string; filename: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: SubmissionReview;
  intakeId: string;
  submissionId: string;
};

const toggled = (set: Set<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

/**
 * Accept and file in one confirmed step: the profile fields that change
 * (old → new, each with Change to save it somewhere else), the uploads added
 * to Documents (AI-flagged ones held back, with a one-click request for a new
 * copy), what stays on this submission (with Casey's suggested field), and an
 * optional case. Nothing is written until the attorney confirms.
 */
export const FinishSheet: FC<Props> = ({
  open,
  onOpenChange,
  review,
  intakeId,
  submissionId
}) => {
  const { clientId, invalidate } = useRequestsIde();
  const navigate = useNavigate();
  const who = review.clientFirstName ?? "your client";
  const whose = review.clientFirstName
    ? `${review.clientFirstName}'s`
    : "the Client's";

  const [view, setView] = useState<OutgoingIntakeSubmissionView | null>(null);
  const [viewVersion, setViewVersion] = useState(0);
  const [matters, setMatters] = useState<MatterDTO[] | null>(null);
  const [caseId, setCaseId] = useState<string>(NO_CASE);
  const [notify, setNotify] = useState(true);
  const [fieldsOff, setFieldsOff] = useState<Set<string>>(new Set());
  const [docsOff, setDocsOff] = useState<Set<string>>(new Set());
  const [newCopy, setNewCopy] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<
    Map<string, OutgoingIntakeTargetSuggestion>
  >(new Map());

  useEffect(() => {
    if (!open) return undefined;
    setFieldsOff(new Set());
    setDocsOff(new Set());
    setNewCopy(new Set());
    let alive = true;
    MattersAPI.listForForeignNational(clientId)
      .then((m) => {
        if (!alive) return;
        setMatters(m);
        const openCases = m.filter((x) => x.case_id && x.individual_id);
        setCaseId(openCases.length === 1 ? openCases[0].case_id : NO_CASE);
      })
      .catch(() => alive && setMatters([]));
    return () => {
      alive = false;
    };
  }, [clientId, open]);

  // The record's current values; reloaded after a mapping changes.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    OutgoingIntakesAPI.getSubmissionView(clientId, intakeId, submissionId)
      .then((v) => alive && setView(v))
      .catch(() => alive && setView(null));
    return () => {
      alive = false;
    };
  }, [clientId, intakeId, open, submissionId, viewVersion]);

  const viewByKey = useMemo(
    () => new Map((view?.answers ?? []).map((a) => [a.answer_key, a])),
    [view]
  );

  const accepted = review.rows.filter((r) => r.decision === "accepted");
  const pending = review.rows.filter((r) => r.decision === "pending");
  const pendingSafe = pending.filter((r) => r.attention === null);

  const fieldRows = accepted.filter(
    (r) =>
      r.savesTo.kind === "record" &&
      r.display !== "" &&
      viewByKey.get(r.key)?.comparison !== "matches"
  );
  const unchangedCount = accepted.filter(
    (r) =>
      r.savesTo.kind === "record" &&
      viewByKey.get(r.key)?.comparison === "matches"
  ).length;
  const docs: DocItem[] = accepted
    .filter((r) => r.savesTo.kind === "vault" && r.attention === null)
    .flatMap((r) =>
      r.files.map((f) => ({
        id: `${r.key}::${f.file_id}`,
        row: r,
        fileId: f.file_id,
        filename: f.filename
      }))
    );
  const held = review.rows.filter(
    (r) =>
      r.savesTo.kind === "vault" &&
      r.attention !== null &&
      r.decision !== "rejected"
  );
  const kept = accepted.filter(
    (r) => r.savesTo.kind === "none" && r.display !== ""
  );
  const keptKeys = kept.map((r) => r.key).join(",");

  // Casey's suggested field for each answer kept on the submission.
  useEffect(() => {
    if (!open || !keptKeys) {
      setSuggestions(new Map());
      return undefined;
    }
    let alive = true;
    OutgoingIntakesAPI.suggestTargets(clientId, intakeId, submissionId, {
      answer_keys: keptKeys.split(",")
    })
      .then(
        (res) =>
          alive &&
          setSuggestions(new Map(res.suggestions.map((s) => [s.answer_key, s])))
      )
      .catch(() => alive && setSuggestions(new Map()));
    return () => {
      alive = false;
    };
  }, [clientId, intakeId, keptKeys, open, submissionId]);

  const remap = async (row: ReviewRow, path: string | null) => {
    await review.mapAnswer(row.node.node_id, path);
    setViewVersion((v) => v + 1);
  };

  const selectedFields = fieldRows.filter((r) => !fieldsOff.has(r.key));
  const selectedDocs = docs.filter((d) => !docsOff.has(d.id));
  const caseMatter = matters?.find((m) => m.case_id === caseId) ?? null;

  const flagged = review.counts.rejected;
  const disabledReason = review.superseded
    ? "A newer submission came in. Review that one instead."
    : flagged
    ? `${flagged} ${
        flagged === 1 ? "answer is" : "answers are"
      } flagged. Send ${flagged === 1 ? "it" : "them"} back or un-flag ${
        flagged === 1 ? "it" : "them"
      } first.`
    : !review.accepted && accepted.length === 0
    ? "Accept at least one answer first."
    : review.accepted &&
      selectedFields.length === 0 &&
      selectedDocs.length === 0 &&
      newCopy.size === 0
    ? "Nothing selected to file."
    : null;

  const run = useCallback(async () => {
    const done: string[] = [];
    try {
      if (!review.accepted) {
        setWorking("Accepting…");
        const res = await OutgoingIntakesAPI.applySubmission(
          clientId,
          intakeId,
          submissionId,
          {
            accept_all: false,
            decisions: review.decisions,
            notify_client: notify
          }
        );
        if (notify && res.email_delivered === false)
          toast.warning("Accepted, but the completion email failed", {
            description: `${who} wasn't told it's complete.`
          });
        done.push("accepted");
      }
      if (selectedFields.length) {
        setWorking("Updating the profile…");
        const res = await OutgoingIntakesAPI.applyAcceptedFields(
          clientId,
          intakeId,
          submissionId,
          {
            answer_keys: selectedFields.map((r) => r.key)
          }
        );
        done.push(
          `${res.applied_count} ${
            res.applied_count === 1 ? "field" : "fields"
          } updated`
        );
        invalidate(["client", "records"]);
      }
      let documentIds: string[] = [];
      if (selectedDocs.length) {
        setWorking("Saving documents…");
        const res = await OutgoingIntakesAPI.saveAcceptedDocuments(
          clientId,
          intakeId,
          submissionId,
          {
            answer_keys: Array.from(
              new Set(selectedDocs.map((d) => d.row.key))
            ),
            file_ids: selectedDocs.map((d) => d.fileId)
          }
        );
        documentIds = res.document_ids;
        done.push(
          `${res.saved_count} ${
            res.saved_count === 1 ? "document" : "documents"
          } saved`
        );
        invalidate(["documents"]);
      }
      if (caseMatter && documentIds.length) {
        setWorking("Filing to the case…");
        await ForeignNationalDocumentsAPI.sendToCase(clientId, {
          individual_id: caseMatter.individual_id,
          case_id: caseMatter.case_id,
          foreign_national_document_ids: documentIds
        });
        done.push(
          `filed to ${String(caseMatter.case?.visa ?? "") || "the case"}`
        );
      }
      for (const row of review.rows.filter((r) => newCopy.has(r.key))) {
        setWorking(`Asking ${who} for a new copy…`);
        // Each request reopens the intake and reissues its link, so they run one at a time.
        // eslint-disable-next-line no-await-in-loop
        const res = await OutgoingIntakesAPI.requestNewCopy(
          clientId,
          intakeId,
          submissionId,
          {
            answer_key: row.key,
            note: row.attention
              ? `The ${row.label.toLowerCase()} you sent couldn't be used: ${
                  row.attention
                }. Please upload a new copy.`
              : `Please upload a new copy of your ${row.label.toLowerCase()}.`
          }
        );
        if (res.email_delivered === false)
          toastEmailFailed(clientId, res.outgoing_intake.outgoing_intake_id);
        done.push(`asked for a new ${row.label.toLowerCase()}`);
      }
      invalidate(["activity"]);
      await review.refresh();
      const href = caseMatter
        ? casePath(matterCaseRef(caseMatter), CASE_HOME_SEGMENT)
        : null;
      toast.success(review.accepted ? "Filed" : "Accepted and filed", {
        description:
          done.filter((d) => d !== "accepted").join(" · ") ||
          "The request is complete.",
        action: href
          ? { label: "Open case", onClick: () => navigate(href) }
          : undefined
      });
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast.error(done.length ? `Stopped partway: ${msg}` : msg, {
        description: done.length
          ? `Done so far: ${done.join(", ")}. Open this again to finish.`
          : undefined
      });
      await review.refresh();
    } finally {
      setWorking(null);
    }
  }, [
    caseMatter,
    clientId,
    intakeId,
    invalidate,
    navigate,
    newCopy,
    notify,
    onOpenChange,
    review,
    selectedDocs,
    selectedFields,
    submissionId,
    who
  ]);

  const recordWas = (r: ReviewRow): string | null => {
    const v = viewByKey.get(r.key);
    if (!v) return null;
    if (v.comparison === "new") return null;
    if (
      v.record_value === undefined ||
      v.record_value === null ||
      v.record_value === ""
    )
      return null;
    return (
      formatGroupAnswerValue(r.node, {
        question_id: r.node.node_id,
        value: v.record_value
      }) || String(v.record_value)
    );
  };
  const mapping = review.busy === "map";

  return (
    <Sheet open={open} onOpenChange={(o) => !working && onOpenChange(o)}>
      <SheetContent
        className="flex w-140 flex-col gap-0 data-[side=right]:sm:max-w-140"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>
            {review.accepted ? "File Accepted Answers" : "Accept and File"}
          </SheetTitle>
          <SheetDescription>
            {review.intake?.title ?? "Intake"} · Submission #
            {review.submissionNumber} · {accepted.length} accepted{" "}
            {accepted.length === 1 ? "answer" : "answers"}. Nothing is written
            until you confirm.
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4">
          {!review.accepted && pending.length ? (
            <div className="flex items-center gap-2.5 rounded-lg bg-fill-1 px-3 py-2 text-sm text-fg-2">
              <span className="min-w-0 flex-1">
                {pending.length}{" "}
                {pending.length === 1 ? "answer is" : "answers are"} still
                pending and won&apos;t be filed.
              </span>
              {pendingSafe.length ? (
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() =>
                    review.acceptKeys(pendingSafe.map((r) => r.key))
                  }
                >
                  <CheckCheck /> Accept {pendingSafe.length} Without Warnings
                </Button>
              ) : null}
            </div>
          ) : null}

          <Section
            icon={<UserCheck />}
            title={`Updates to ${whose} Profile`}
            trailing={
              fieldRows.length
                ? `${selectedFields.length} of ${fieldRows.length} selected`
                : undefined
            }
          >
            {fieldRows.length ? (
              <div
                className="divide-y divide-line-1 rounded-xl ring-1 ring-line-2"
                data-testid="finish-profile"
              >
                {fieldRows.map((r) => {
                  const was = recordWas(r);
                  return (
                    <div
                      key={r.key}
                      className="flex items-start gap-2.5 px-3 py-2"
                    >
                      <Checkbox
                        checked={!fieldsOff.has(r.key)}
                        onCheckedChange={() =>
                          setFieldsOff((s) => toggled(s, r.key))
                        }
                        aria-label={`Update ${
                          r.savesTo.kind === "record" ? r.savesTo.label : ""
                        }`}
                        className="mt-0.5"
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-sm text-fg-3">
                          {r.savesTo.kind === "record" ? r.savesTo.label : ""}
                          {r.context ? ` · ${r.context}` : ""}
                        </span>
                        <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-base">
                          {was ? (
                            <>
                              <span className="break-words text-fg-3 line-through">
                                {was}
                              </span>
                              <ArrowRight className="size-3 shrink-0 text-fg-4" />
                            </>
                          ) : null}
                          <span className="break-words text-fg">
                            {r.display}
                          </span>
                          {was ? null : (
                            <span className="text-sm text-fg-3">(New)</span>
                          )}
                        </span>
                      </div>
                      <SaveToPicker
                        value={
                          r.savesTo.kind === "record" ? r.savesTo.path : null
                        }
                        questionType={r.node.question_type}
                        onPick={(path) => void remap(r, path)}
                        align="end"
                      >
                        <Button variant="ghost" size="xs" disabled={mapping}>
                          Change
                        </Button>
                      </SaveToPicker>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-sm text-fg-3">
                {unchangedCount
                  ? `Nothing to change: ${unchangedCount} mapped ${
                      unchangedCount === 1
                        ? "answer already matches"
                        : "answers already match"
                    } the profile.`
                  : "No accepted answer updates the profile."}
              </span>
            )}
          </Section>

          <Section
            icon={<FolderInput />}
            title="Added to Documents"
            trailing={
              docs.length
                ? `${selectedDocs.length} of ${docs.length}`
                : undefined
            }
          >
            <div className="flex flex-col gap-1.5">
              {docs.length ? (
                <div className="divide-y divide-line-1 rounded-xl ring-1 ring-line-2">
                  {docs.map((d) => (
                    <Label
                      key={d.id}
                      className="flex h-9 cursor-pointer items-center gap-2.5 px-3 text-base font-normal"
                    >
                      <Checkbox
                        checked={!docsOff.has(d.id)}
                        onCheckedChange={() =>
                          setDocsOff((s) => toggled(s, d.id))
                        }
                      />
                      <span className="w-44 shrink-0 truncate text-fg">
                        {d.row.label}
                      </span>
                      <span className="inline-flex min-w-0 items-center gap-1 truncate text-sm text-fg-3">
                        <Paperclip className="size-3 shrink-0" />
                        {d.filename}
                      </span>
                    </Label>
                  ))}
                </div>
              ) : null}
              {!docs.length && !held.length ? (
                <span className="text-sm text-fg-3">No accepted uploads.</span>
              ) : null}
              {held.map((r) => {
                const queued = newCopy.has(r.key);
                return (
                  <div
                    key={r.key}
                    className="flex items-center gap-2.5 rounded-lg bg-warn-soft px-3 py-2"
                  >
                    <AlertTriangle className="size-3.5 shrink-0 text-warn" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-base text-warn">
                        {r.label} held back
                      </span>
                      <span className="text-sm text-warn">
                        {queued
                          ? `${who} gets a one-question request for a new copy.`
                          : `AI check: ${r.attention}`}
                      </span>
                    </div>
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => setNewCopy((s) => toggled(s, r.key))}
                    >
                      {queued ? (
                        <>
                          <Undo2 /> Don&apos;t Ask
                        </>
                      ) : (
                        "Ask for a New Copy"
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </Section>

          {kept.length ? (
            <Section
              icon={<FileText />}
              title="Kept on This Submission"
              trailing={`${kept.length} ${
                kept.length === 1 ? "answer" : "answers"
              }`}
            >
              <div
                className="divide-y divide-line-1 rounded-xl ring-1 ring-line-2"
                data-testid="finish-kept"
              >
                {kept.map((r) => {
                  const suggestion = suggestions.get(r.key);
                  return (
                    <div
                      key={r.key}
                      className="flex flex-col gap-1.5 px-3 py-2"
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="text-sm text-fg-3">{r.label}</span>
                          <span className="text-base break-words text-fg">
                            {r.display}
                          </span>
                        </div>
                        <SaveToPicker
                          value={null}
                          questionType={r.node.question_type}
                          onPick={(path) => void remap(r, path)}
                          align="end"
                        >
                          <Button variant="ghost" size="xs" disabled={mapping}>
                            Save to a Field…
                          </Button>
                        </SaveToPicker>
                      </div>
                      {suggestion ? (
                        <div className="flex items-center gap-1.5 self-start rounded-full bg-accent-soft py-0.5 pr-1 pl-2 text-sm text-accent">
                          <Sparkles className="size-3" />
                          <span>
                            {ASSISTANT_NAME} suggests: {suggestion.label}
                          </span>
                          <Button
                            variant="ghost"
                            size="xs"
                            disabled={mapping}
                            onClick={() => void remap(r, suggestion.model_path)}
                          >
                            Use
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <span className="text-sm text-fg-3">
                These stay on the submission. Save one to a field to update the
                profile with it.
              </span>
            </Section>
          ) : null}

          <Section icon={<Briefcase />} title="Also File to a Case">
            <Select
              value={caseId}
              onValueChange={setCaseId}
              disabled={!matters?.length}
            >
              <SelectTrigger size="sm" className="w-full" aria-label="Case">
                <SelectValue
                  placeholder={
                    matters === null
                      ? "Loading cases…"
                      : "No cases for this client"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(matters ?? [])
                  .filter((m) => m.case_id && m.individual_id)
                  .map((m) => (
                    <SelectItem key={m.case_id} value={m.case_id}>
                      {matterLabel(m)}
                    </SelectItem>
                  ))}
                <SelectItem value={NO_CASE}>
                  Don&apos;t File to a Case
                </SelectItem>
              </SelectContent>
            </Select>
            {caseMatter && !selectedDocs.length ? (
              <span className="text-sm text-fg-3">
                No documents selected, so nothing goes to the case.
              </span>
            ) : null}
          </Section>
        </div>

        <SheetFooter className="flex-col items-stretch gap-2 border-t border-line-1">
          {disabledReason ? (
            <span className="text-right text-sm text-fg-3">
              {disabledReason}
            </span>
          ) : null}
          <div className="flex items-center gap-2">
            {!review.accepted ? (
              <Label className="mr-auto text-sm font-normal text-fg-2">
                <Switch checked={notify} onCheckedChange={setNotify} /> Email{" "}
                {who} that it&apos;s complete
              </Label>
            ) : (
              <span className="mr-auto" />
            )}
            <Button
              variant="ghost"
              disabled={Boolean(working)}
              onClick={() => onOpenChange(false)}
            >
              Back to Review
            </Button>
            <Button
              variant="primary"
              disabled={Boolean(working) || Boolean(disabledReason)}
              onClick={() => void run()}
            >
              {working ? <Loader2 className="animate-spin" /> : <Check />}
              {working ?? (review.accepted ? "File" : "Accept and File")}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
